/**
 * Phase 11.2.1 (Bug N1) — German cities that maintain a Berufsfeuerwehr.
 *
 * Heuristic input: classifyFireStation falls back to this list when the name
 * tag is unspecific (e.g. "Feuerwache 4"). When the player city name matches
 * any entry here, fire stations are biased toward bf-station unless the name
 * explicitly says "FF" or "Freiwillig".
 *
 * Source: deutsche Berufsfeuerwehren (>100k population mostly), as of 2025.
 * Not exhaustive but covers the major BFs the game players most likely select.
 */
export const KNOWN_BF_CITIES: ReadonlyArray<string> = [
  'Berlin',
  'Hamburg',
  'München',
  'Munich',
  'Köln',
  'Cologne',
  'Frankfurt am Main',
  'Frankfurt',
  'Stuttgart',
  'Düsseldorf',
  'Leipzig',
  'Dortmund',
  'Essen',
  'Bremen',
  'Dresden',
  'Hannover',
  'Nürnberg',
  'Nuremberg',
  'Duisburg',
  'Bochum',
  'Wuppertal',
  'Bielefeld',
  'Bonn',
  'Münster',
  'Karlsruhe',
  'Mannheim',
  'Augsburg',
  'Wiesbaden',
  'Mönchengladbach',
  'Gelsenkirchen',
  'Aachen',
  'Braunschweig',
  'Kiel',
  'Chemnitz',
  'Halle (Saale)',
  'Halle',
  'Magdeburg',
  'Freiburg im Breisgau',
  'Freiburg',
  'Krefeld',
  'Mainz',
  'Lübeck',
  'Erfurt',
  'Oberhausen',
  'Rostock',
  'Kassel',
  'Saarbrücken',
  'Hagen',
  'Potsdam',
  'Hamm',
  'Mülheim an der Ruhr',
  'Ludwigshafen am Rhein',
  'Ludwigshafen',
  'Leverkusen',
  'Oldenburg',
  'Osnabrück',
  'Solingen',
  'Heidelberg',
  'Herne',
  'Neuss',
  'Darmstadt',
  'Paderborn',
  'Regensburg',
  'Würzburg',
  'Ingolstadt',
  'Pforzheim',
  'Reutlingen',
  // Austria/CH equivalents (Berufsfeuerwehr-like) — included for parity.
  'Wien',
  'Vienna',
  'Graz',
  'Linz',
  'Salzburg',
  'Innsbruck',
  'Zürich',
  'Zurich',
  'Basel',
  'Bern',
  'Genf',
  'Genève',
  'Lausanne',
];

const NORMALIZED = new Set(KNOWN_BF_CITIES.map((s) => s.toLowerCase()));

/**
 * Returns true when the given city display name (or one of its tokens) is in
 * the BF-cities list. Robust to "Berlin, Berlin, Deutschland" formatting.
 */
export function cityHasBerufsfeuerwehr(displayName: string | undefined): boolean {
  if (!displayName) return false;
  const lower = displayName.toLowerCase();
  if (NORMALIZED.has(lower)) return true;
  // Take the first comma-segment.
  const first = lower.split(',')[0]?.trim();
  if (first && NORMALIZED.has(first)) return true;
  // Substring match (handles "Stadt München" style city names).
  for (const known of NORMALIZED) {
    if (lower.includes(known)) return true;
  }
  return false;
}
